// client/src/components/admin/AdminNotifications.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // For system notification form
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    recipientType: 'all'
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Add timestamp to prevent caching issues
        const res = await axios.get(`/api/notifications?t=${Date.now()}`);
        
        // Validate response format
        if (res.data && Array.isArray(res.data.notifications)) {
          setNotifications(res.data.notifications);
        } else {
          console.error('Unexpected response format:', res.data);
          setError('Received unexpected data format from server');
        }
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Failed to fetch notifications: ${err.response?.data?.message || err.message}`);
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Set up polling interval
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);
  
  const markAllAsRead = async () => {
    try {
      const res = await axios.put('/api/notifications/read-all');
      
      if (res.status === 200) {
        setNotifications(notifications.map(notification => ({
          ...notification,
          isRead: true
        })));
      } else {
        console.error('Unexpected response:', res);
        setError('Failed to mark notifications as read: Unexpected response');
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError(`Failed to mark notifications as read: ${err.response?.data?.message || err.message}`);
    }
  };
  
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear any previous errors/success messages when form is edited
    setFormError('');
    setFormSuccess('');
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setFormSubmitting(true);
    
    if (!formData.title.trim() || !formData.message.trim()) {
      setFormError('Please fill in all fields');
      setFormSubmitting(false);
      return;
    }
    
    try {
      // Send the actual request to the server
      const res = await axios.post('/api/notifications/send', {
        title: formData.title,
        message: formData.message,
        recipientType: formData.recipientType
      });
      
      if (res.status === 200 || res.status === 201) {
        setFormSuccess('System notification sent successfully');
        // Reset form
        setFormData({
          title: '',
          message: '',
          recipientType: 'all'
        });
        
        // Refresh notifications to show the newly sent one
        const notifRes = await axios.get(`/api/notifications?t=${Date.now()}`);
        if (notifRes.data && Array.isArray(notifRes.data.notifications)) {
          setNotifications(notifRes.data.notifications);
        }
      } else {
        setFormError('Unexpected response from server');
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      setFormError(`Failed to send notification: ${err.response?.data?.message || err.message}`);
    } finally {
      setFormSubmitting(false);
    }
  };
  
  // Function to format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };
  
  // Function to format time for display
  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Error formatting time:', err);
      return '';
    }
  };
  
  // Group notifications by date
  const groupNotificationsByDate = () => {
    const groups = {};
    
    notifications.forEach(notification => {
      if (!notification.createdAt) return;
      
      const date = new Date(notification.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(groups)
      .map(([date, items]) => ({ date, items }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };
  
  const notificationGroups = groupNotificationsByDate();
  
  // Custom styles to override the existing CSS
  const customStyles = {
    formContainer: {
      padding: '20px',
      marginBottom: '20px'
    },
    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '20px'
    },
    notificationItem: {
      padding: '15px',
      borderBottom: '1px solid var(--border)',
      backgroundColor: 'white'
    },
    unreadItem: {
      backgroundColor: 'var(--primary-light)',
      borderLeft: '4px solid var(--primary)'
    },
    notificationHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '6px'
    },
    notificationTitle: {
      fontWeight: '600',
      fontSize: '16px',
      color: 'var(--dark)'
    },
    notificationDate: {
      fontSize: '12px',
      color: 'var(--grey)'
    },
    notificationMessage: {
      margin: '0',
      color: 'var(--grey)',
      lineHeight: '1.5'
    },
    dateHeader: {
      padding: '10px 15px',
      backgroundColor: 'var(--light-grey)',
      fontWeight: '500',
      color: 'var(--dark)',
      borderBottom: '1px solid var(--border)'
    },
    notificationType: {
      fontSize: '12px',
      color: 'var(--grey)',
      marginTop: '8px',
      padding: '2px 6px',
      backgroundColor: 'var(--light-grey)',
      borderRadius: '4px',
      display: 'inline-block'
    }
  };
  
  if (loading) {
    return <div className="loading">Loading notifications...</div>;
  }
  
  return (
    <div className="admin-notifications">
      <h2>Notifications</h2>
      
      <div className="card">
        <div className="card-title">
          <span>Send System Notification</span>
        </div>
        
        {formError && <div className="alert-danger">{formError}</div>}
        {formSuccess && <div className="alert-success">{formSuccess}</div>}
        
        <form onSubmit={handleSubmit} style={customStyles.formContainer}>
          <div className="form-group">
            <label htmlFor="title">Notification Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={formSubmitting}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Notification Message</label>
            <textarea
              id="message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleInputChange}
              disabled={formSubmitting}
              required
            ></textarea>
          </div>
          
          <div className="form-group">
            <label htmlFor="recipientType">Recipients</label>
            <select
              id="recipientType"
              name="recipientType"
              value={formData.recipientType}
              onChange={handleInputChange}
              disabled={formSubmitting}
              required
            >
              <option value="all">All Users</option>
              <option value="banks">All Bank Users</option>
              <option value="companies">All Company Users</option>
            </select>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={formSubmitting}
            >
              {formSubmitting ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </form>
      </div>
      
      <div className="card">
        <div className="card-title">
          <span>Your Notifications</span>
          {notifications.some(n => !n.isRead) && (
            <button 
              className="btn btn-light btn-sm"
              onClick={markAllAsRead}
            >
              Mark All as Read
            </button>
          )}
        </div>
        
        {error && <div className="alert-danger">{error}</div>}
        
        <div>
          {notificationGroups.length > 0 ? (
            notificationGroups.map((group, groupIndex) => (
              <div key={group.date}>
                <div style={customStyles.dateHeader}>
                  {formatDate(group.date)}
                </div>
                {group.items.map((notification) => (
                  <div 
                    key={notification._id || `notification-${groupIndex}-${Math.random()}`} 
                    style={{
                      ...customStyles.notificationItem,
                      ...(notification.isRead ? {} : customStyles.unreadItem)
                    }}
                  >
                    <div style={customStyles.notificationHeader}>
                      <span style={customStyles.notificationTitle}>
                        {notification.title || 'No Title'}
                      </span>
                      <span style={customStyles.notificationDate}>
                        {formatTime(notification.createdAt)}
                      </span>
                    </div>
                    <p style={customStyles.notificationMessage}>
                      {notification.message || 'No message content'}
                    </p>
                    {notification.relatedEntity && (
                      <div style={customStyles.notificationType}>
                        {notification.relatedEntity.type === 'request' ? 'Request' : 
                         notification.relatedEntity.type === 'bid' ? 'Bid' : 
                         notification.relatedEntity.type === 'user' ? 'User' : 
                         notification.relatedEntity.type}
                        {notification.relatedEntity.id && ` #${notification.relatedEntity.id}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--grey)' }}>
              <p>No notifications found</p>
            </div>
          )}
          
          {/* Add a styled "View All" button that matches your design system */}
          {notificationGroups.length > 3 && (
            <div style={{ textAlign: 'center', padding: '15px', borderTop: '1px solid var(--border)' }}>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/admin/notifications')}
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications;